import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { navegarComCtrl } from "../../utils/solicitacoes/navegacao";
import Chart from "react-apexcharts";
import { CustomTable } from "../../componentes/table/CustomTable";
import { CustomDropdown } from "../../componentes/dropdown/CustomDropdown";
import StatusBadge from "../../components/solicitacoes/Shared/StatusBadge";
import { ChartCard } from "../../componentes/chart/CardChart";
import TabelaEntriesGrafico from "../../components/solicitacoes/Dashboard/TabelaEntriesGrafico";
import DashboardProcessoLayout, { Campo, INPUT_CLASS } from "../../components/solicitacoes/Dashboard/DashboardProcessoLayout";
import { useDashboardProcesso } from "../../hooks/solicitacoes/useDashboardProcesso";
import PromocaoFuncionarioService from "../../services/resource/promocaoFuncionarioService";
import UsuarioService from "../../services/resource/usuarioService";
import { STATUS_CONFIG } from "../../utils/solicitacoes/statusConfig";
import { donutOptions, barOptions, CORES_GRAFICO } from "../../utils/solicitacoes/chartOptions";

const service = new PromocaoFuncionarioService();
const usuarioService = new UsuarioService();

const STATUS_OPTIONS = Object.entries(STATUS_CONFIG).map(([value, info]) => ({ value, label: info.label }));

const TIPO_MUDANCA_OPTIONS = [
    { value: "PROMOCAO", label: "Promoção" },
    { value: "TRANSFERENCIA", label: "Transferência" },
    { value: "ENQUADRAMENTO", label: "Enquadramento" },
];

const FILTROS_INICIAIS_EXTRA = {
    funcionarioNome: "", cargoAtual: "", cargoProposto: "", setor: "", status: null,
    solicitanteId: null, dataPrevistaInicial: "", dataPrevistaFinal: "", tipoMudanca: null,
};

function tiposMudancaDaLinha(row) {
    const tipos = [];
    if (row.incluiPromocao) tipos.push("Promoção");
    if (row.incluiTransferencia) tipos.push("Transferência");
    if (row.incluiEnquadramento) tipos.push("Enquadramento");
    return tipos.join(", ");
}

const simNao = v => v == null ? "-" : (v ? "Sim" : "Não");

const COLUNAS = [
    { key: "funcionarioNome", label: "Funcionário" },
    { key: "funcionarioCargoOriginal", label: "Cargo Original" },
    { key: "unidadeNome", label: "Filial" },
    { key: "tipoMudanca", label: "Tipo de Mudança", sortable: false, filterable: false, render: (v, row) => tiposMudancaDaLinha(row) },
    { key: "cargoAtual", label: "Cargo Atual" },
    { key: "cargoProposto", label: "Cargo Proposto" },
    { key: "setorAtual", label: "Setor Atual" },
    { key: "mudaDeSetor", label: "Muda de Setor", render: simNao, center: true },
    { key: "setorProposto", label: "Setor Proposto" },
    { key: "salarioAtual", label: "Salário Atual", format: "currency" },
    { key: "salarioProposto", label: "Salário Proposto", format: "currency" },
    { key: "dataPrevistaMudanca", label: "Data Prevista", format: "date" },
    { key: "filialPropostaNome", label: "Filial de Destino" },
    { key: "escalaAtual", label: "Escala Atual" },
    { key: "escalaProposta", label: "Escala Proposta" },
    { key: "beneficios", label: "Benefícios" },
    { key: "justificativa", label: "Justificativa" },
    { key: "justificativaAnalise", label: "Justificativa (Análise RH)" },
    { key: "comentarioAprovacaoGestor", label: "Comentário (Gestor)" },
    { key: "status", label: "Status", render: v => <StatusBadge status={v} />, center: true },
    { key: "solicitanteNome", label: "Solicitante" },
    { key: "dataSolicitacao", label: "Data da Solicitação", format: "date" },
];

export default function RelatorioPromocoes() {
    const navigate = useNavigate();
    const [usuarios, setUsuarios] = useState([]);

    const { filtros, setFiltros, limparFiltros, pesquisar, linhas, dashboard, loading } = useDashboardProcesso({
        service, chaveFiltros: "solicitacoes.promocoes.filtros", filtrosIniciaisExtra: FILTROS_INICIAIS_EXTRA,
    });

    useEffect(() => {
        document.title = "MyDash | Relatório de Promoção, Enquadramento e Transferência";
    }, []);

    useEffect(() => {
        usuarioService.listarUsuarios().then(res => setUsuarios(res.data)).catch(() => setUsuarios([]));
    }, []);

    function setFiltro(campo, valor) {
        setFiltros(prev => ({ ...prev, [campo]: valor }));
    }

    const porSetorEntries = Object.entries(dashboard?.porSetor || {});
    const porCargoEntries = Object.entries(dashboard?.porCargo || {});
    const evolucaoEntries = Object.entries(dashboard?.evolucaoMensal || {});
    const aprovacaoEntries = [["Aprovadas", dashboard?.aprovadas ?? 0], ["Reprovadas", dashboard?.reprovadas ?? 0]];

    const [viewSetor, setViewSetor] = useState("chart");
    const [viewCargo, setViewCargo] = useState("chart");
    const [viewAprovacao, setViewAprovacao] = useState("chart");
    const [viewEvolucao, setViewEvolucao] = useState("chart");

    return (
        <DashboardProcessoLayout
            titulo="Relatório de Promoção, Enquadramento e Transferência"
            onNovaSolicitacao={() => navigate("/solicitacoes/nova")}
            loading={loading}
            filtros={filtros}
            onFiltroChange={setFiltro}
            onPesquisar={pesquisar}
            onLimparFiltros={limparFiltros}
            kpis={[
                { label: "Total de solicitações", value: dashboard?.total ?? 0, icon: "fa fa-user-graduate", iconClassName: "bg-primary/10 text-primary" },
                { label: "Aprovadas", value: dashboard?.aprovadas ?? 0, icon: "fa fa-check-circle", iconClassName: "bg-success/10 text-success" },
                { label: "Reprovadas", value: dashboard?.reprovadas ?? 0, icon: "fa fa-times-circle", iconClassName: "bg-destructive/10 text-destructive" },
                { label: "Em análise", value: dashboard?.emAnalise ?? 0, icon: "fa fa-spinner", iconClassName: "bg-amber-100 text-amber-700" },
                { label: "Com promoção", value: dashboard?.totalComPromocao ?? 0, icon: "fa fa-user-graduate", iconClassName: "bg-blue-100 text-blue-700" },
                { label: "Com transferência", value: dashboard?.totalComTransferencia ?? 0, icon: "fa fa-people-arrows", iconClassName: "bg-indigo-100 text-indigo-700" },
                { label: "Com enquadramento", value: dashboard?.totalComEnquadramento ?? 0, icon: "fa fa-calendar-alt", iconClassName: "bg-purple-100 text-purple-700" },
            ]}
            graficos={(
                <>
                    <div className="lg:col-span-4">
                        <ChartCard title="Promoções por setor" view={viewSetor} onViewChange={setViewSetor} accent={CORES_GRAFICO[0]}
                            tableContent={<TabelaEntriesGrafico entries={porSetorEntries} colunaValor="Quantidade" />}>
                            {porSetorEntries.length === 0 ? (
                                <p className="text-sm text-muted-foreground flex items-center justify-center h-64">Sem dados para o filtro atual.</p>
                            ) : (
                                <Chart type="donut" height="100%" series={porSetorEntries.map(([, v]) => v)}
                                    options={donutOptions(porSetorEntries.map(([k]) => k))} />
                            )}
                        </ChartCard>
                    </div>
                    <div className="lg:col-span-4">
                        <ChartCard title="Promoções por cargo" view={viewCargo} onViewChange={setViewCargo} accent={CORES_GRAFICO[1]}
                            tableContent={<TabelaEntriesGrafico entries={porCargoEntries} colunaValor="Quantidade" />}>
                            {porCargoEntries.length === 0 ? (
                                <p className="text-sm text-muted-foreground flex items-center justify-center h-64">Sem dados para o filtro atual.</p>
                            ) : (
                                <Chart type="donut" height="100%" series={porCargoEntries.map(([, v]) => v)}
                                    options={donutOptions(porCargoEntries.map(([k]) => k))} />
                            )}
                        </ChartCard>
                    </div>
                    <div className="lg:col-span-4">
                        <ChartCard title="Aprovação x Reprovação" view={viewAprovacao} onViewChange={setViewAprovacao} accent={CORES_GRAFICO[2]}
                            tableContent={<TabelaEntriesGrafico entries={aprovacaoEntries} colunaValor="Quantidade" />}>
                            {(dashboard?.aprovadas ?? 0) + (dashboard?.reprovadas ?? 0) === 0 ? (
                                <p className="text-sm text-muted-foreground flex items-center justify-center h-64">Sem dados para o filtro atual.</p>
                            ) : (
                                <Chart type="donut" height="100%" series={[dashboard?.aprovadas ?? 0, dashboard?.reprovadas ?? 0]}
                                    options={donutOptions(["Aprovadas", "Reprovadas"], {
                                        colors: ["#10B981", "#EF4444"],
                                        dataLabels: { formatter: val => `${val.toFixed(0)}%` },
                                    })} />
                            )}
                        </ChartCard>
                    </div>
                    <div className="lg:col-span-12">
                        <ChartCard title="Evolução mensal das promoções" view={viewEvolucao} onViewChange={setViewEvolucao} accent={CORES_GRAFICO[3]}
                            tableContent={<TabelaEntriesGrafico entries={evolucaoEntries} colunaValor="Quantidade" />}>
                            {evolucaoEntries.length === 0 ? (
                                <p className="text-sm text-muted-foreground flex items-center justify-center h-64">Sem dados para o filtro atual.</p>
                            ) : (
                                <Chart type="bar" height="100%" series={[{ name: "Solicitações", data: evolucaoEntries.map(([, v]) => v) }]}
                                    options={barOptions(evolucaoEntries.map(([k]) => k))} />
                            )}
                        </ChartCard>
                    </div>
                </>
            )}
            filtrosExtras={(
                <>
                    <Campo label="Funcionário">
                        <input type="text" value={filtros.funcionarioNome} onChange={e => setFiltro("funcionarioNome", e.target.value)}
                            placeholder="Buscar por nome" className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Cargo atual">
                        <input type="text" value={filtros.cargoAtual} onChange={e => setFiltro("cargoAtual", e.target.value)} className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Cargo proposto">
                        <input type="text" value={filtros.cargoProposto} onChange={e => setFiltro("cargoProposto", e.target.value)} className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Setor">
                        <input type="text" value={filtros.setor} onChange={e => setFiltro("setor", e.target.value)} className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Tipo de mudança">
                        <CustomDropdown value={filtros.tipoMudanca} onChange={e => setFiltro("tipoMudanca", e.value)} options={TIPO_MUDANCA_OPTIONS}
                            placeholder="Todos os tipos" showClear />
                    </Campo>
                    <Campo label="Status">
                        <CustomDropdown value={filtros.status} onChange={e => setFiltro("status", e.value)} options={STATUS_OPTIONS}
                            placeholder="Todos os status" showClear />
                    </Campo>
                    <Campo label="Solicitante">
                        <CustomDropdown value={filtros.solicitanteId} onChange={e => setFiltro("solicitanteId", e.value)} options={usuarios}
                            optionValue="id" optionLabel="nome" placeholder="Todos os solicitantes" showClear filter />
                    </Campo>
                    <Campo label="Mudança prevista de">
                        <input type="date" value={filtros.dataPrevistaInicial} onChange={e => setFiltro("dataPrevistaInicial", e.target.value)} className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Mudança prevista até">
                        <input type="date" value={filtros.dataPrevistaFinal} onChange={e => setFiltro("dataPrevistaFinal", e.target.value)} className={INPUT_CLASS} />
                    </Campo>
                </>
            )}
        >
            <CustomTable
                data={linhas}
                columns={COLUNAS}
                loading={loading}
                pagination
                pageSize={20}
                exportable
                showHeader={false}
                className="text-[11px] border shadow-card rounded-xl"
                exportFilename="relatorio_promocoes"
                footer={() => null}
                onRowSelect={(row, e) => navegarComCtrl(navigate, `/solicitacoes/${row.solicitacaoId}`, e)}
            />
        </DashboardProcessoLayout>
    );
}
